import { Command } from 'commander';

import { graphql, graphqlUnauthenticated } from '../graphql/client.ts';
import { Me } from '../graphql/operations/common.ts';
import { DeviceAuthorize, DeviceToken } from '../graphql/operations/hermes.ts';
import { saveCredentials } from '../lib/auth.ts';
import { openBrowser } from '../lib/browser.ts';
import { error, hint, info, success, warn } from '../lib/output.ts';
import { servicesWithoutScope } from '../lib/token-claims.ts';

// TODO: fetch the set of available API scopes dynamically (e.g. from Hermes'
// resource server) once the backend exposes them. For now the CLI requests a
// fixed superset so users can exercise every subgraph. Auth0 RBAC grants the
// intersection of requested scopes and the user's assigned permissions by EXACT
// string match — wildcards are not expanded, and unmatched scopes are dropped
// silently. The list below must therefore contain every permission name the
// terraform-auth0 roles can hold (superadmin `*:*`, admin `*:read`, sales
// granular grants), not just the broadest wildcard per service.
const API_SCOPES = [
  'atlas/*:*',
  'atlas/*:read',
  'core/*:*',
  'core/*:read',
  'signals/*:*',
  'signals/*:read',
  'billing/*:*',
  'billing/*:read',
  'billing/offer:*',
  'outreach/*:*',
  'outreach/*:read',
  'hermes/*:*',
  'hermes/*:read',
];
const DEFAULT_SCOPE = `openid profile email offline_access ${API_SCOPES.join(' ')}`;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function setupUserContext(endpoint?: string): Promise<void> {
  const userData = await graphql(Me, undefined, { endpoint });
  const user = userData.me;
  if (!user) return;

  info('');
  info(`  User:     ${user.firstName} ${user.lastName} <${user.email}>`);
  hint('Run `sl whoami` to verify.');
}

/**
 * Says so when the identity provider granted nothing for a service.
 *
 * Without this the loss is invisible: login reports success, and the operator
 * meets it much later as `unknown command 'organizations'`, because the CLI
 * builds its commands from the schema the router filters by the token's scopes.
 */
function reportScopeGrant(requestedScope: string, accessToken: string): void {
  const missing = servicesWithoutScope(requestedScope, accessToken);
  if (missing.length === 0) return;

  warn(`No scope granted for: ${missing.join(', ')}.`);
  info('  Their commands will not appear in `sl --help`, and their fields are');
  info('  filtered out of the schema this CLI builds itself from.');
  hint('Auth0 grants requested scopes by exact string match against your assigned');
  hint('permissions and drops the rest silently — this is a role assignment in the');
  hint('tenant you logged into, not a CLI setting. Ask an administrator, then re-run `sl login`.');
}

export const loginCommand = new Command('login')
  .summary('Log in to your Sortlist account')
  .description(
    'Log in to your Sortlist account\n\n' +
      'Opens a browser to complete authentication, then saves your\n' +
      "credentials locally. If you belong to multiple companies, you'll\n" +
      'be prompted to pick one.\n\n' +
      'Examples:\n' +
      '  sl login                    Log in with default settings\n' +
      '  sl login --no-browser       Print the login URL instead of opening it',
  )
  .option('--scope <scope>', 'OAuth scope', DEFAULT_SCOPE)
  .option('--no-browser', 'Print the login URL instead of opening a browser')
  .action(async function (this: Command) {
    const opts = this.optsWithGlobals();

    info('Starting device authorization...');

    const authData = await graphqlUnauthenticated(DeviceAuthorize, { input: { scope: opts.scope } }, opts.endpoint);

    const device = authData.deviceAuthorize;
    if (!device) {
      error('Device authorization failed — the server returned an empty response.');
      hint('Check your network connection and try again.');
      hint('If the problem persists, verify the endpoint: sl config show');
      process.exit(2);
    }

    const url = device.verificationUriComplete ?? device.verificationUri;
    info('');
    info(`  Open this URL in your browser:`);
    info(`  ${url}`);
    info('');
    info(`  Enter code: ${device.userCode}`);
    info('');

    if (opts.browser) {
      openBrowser(url);
      info('  (Browser opened automatically)');
      info('');
    }

    info('Waiting for you to complete login in the browser...');

    let interval = device.interval * 1000;
    const deadline = Date.now() + device.expiresIn * 1000;

    while (Date.now() < deadline) {
      await sleep(interval);

      const tokenData = await graphqlUnauthenticated(
        DeviceToken,
        { input: { deviceCode: device.deviceCode } },
        opts.endpoint,
      );

      const token = tokenData.deviceToken;
      if (!token) {
        error('Token exchange failed — the server returned an empty response.');
        hint('Try running `sl login` again.');
        process.exit(2);
      }

      if (token.error === 'authorization_pending') {
        continue;
      }

      if (token.error === 'slow_down') {
        interval += 5000;
        continue;
      }

      if (token.error === 'expired_token') {
        error('The login code expired before you completed authentication.');
        hint('Run `sl login` to generate a new code.');
        process.exit(2);
      }

      if (token.error === 'access_denied') {
        error('Access was denied during authentication.');
        hint('Make sure you approved the request in the browser.');
        hint('If you denied it by mistake, run `sl login` to try again.');
        process.exit(2);
      }

      if (token.error) {
        error(`Authentication failed (${token.error}).`);
        hint('Run `sl login` to try again.');
        process.exit(2);
      }

      if (token.accessToken && token.refreshToken && token.idToken && token.expiresIn) {
        saveCredentials({
          accessToken: token.accessToken,
          refreshToken: token.refreshToken,
          idToken: token.idToken,
          expiresAt: Math.floor(Date.now() / 1000) + token.expiresIn,
        });

        success('Logged in successfully.');
        reportScopeGrant(opts.scope, token.accessToken);
        await setupUserContext(opts.endpoint);
        return;
      }
    }

    error('Authentication timed out — no response from the browser.');
    hint('Run `sl login` to start a new session.');
    process.exit(2);
  });
